/**
 * Foundation · Durate — UNICO punto di conversione, validazione, normalizzazione
 * e visualizzazione delle durate della GI Suite (M-UX-05).
 *
 * Rappresentazione interna unica: ore centesimali (Number).
 * Formati di input/visualizzazione per azienda:
 *   "decimal" → 7,50  (comportamento storico, invariato: 2 decimali HALF_UP)
 *   "hm"      → 7:30  (memorizzazione interna a 6 decimali: nessuna deriva nelle somme)
 */
export const DURATION_FORMATS = Object.freeze({ DECIMAL: "decimal", HM: "hm" });
export const DEFAULT_DURATION_FORMAT = DURATION_FORMATS.DECIMAL;
export const DURATION_STORAGE_DECIMALS = Object.freeze({ decimal: 2, hm: 6 });
export const DURATION_FORMAT_BADGE = Object.freeze({ decimal: "1/100", hm: "HH:MM" });
export const DURATION_FORMAT_OPTIONS = Object.freeze([
  { value: "decimal", label: "Ore centesimali (es. 7,50)" },
  { value: "hm", label: "Ore e minuti (es. 7:30)" },
]);

export const normalizeDurationFormat = (fmt) => (fmt === DURATION_FORMATS.HM ? DURATION_FORMATS.HM : DURATION_FORMATS.DECIMAL);

/** Arrotondamento HALF_UP della memorizzazione interna secondo il formato. */
export function roundDurationStorage(hours, fmt = DEFAULT_DURATION_FORMAT) {
  const n = Number(hours);
  if (!Number.isFinite(n)) return n;
  const f = 10 ** DURATION_STORAGE_DECIMALS[normalizeDurationFormat(fmt)];
  const sign = n < 0 ? -1 : 1;
  return (sign * Math.round((Math.abs(n) + Number.EPSILON) * f)) / f;
}

const HM_ERROR = "Formato non valido: usa ore:minuti (es. 7:30)";
const HM_MINUTES_ERROR = "Minuti non validi (0-59): usa ore:minuti (es. 7:30)";
const DEC_ERROR = "Formato non valido: usa ore centesimali (es. 7,50)";

/**
 * Converte il testo digitato in ore centesimali.
 * @returns {{value: number|null, error: string|null}} value già arrotondato per la memorizzazione.
 */
export function parseDuration(raw, fmt = DEFAULT_DURATION_FORMAT, { allowNegative = false } = {}) {
  if (raw === null || raw === undefined) return { value: null, error: null };
  if (typeof raw === "number") {
    return Number.isFinite(raw) && (allowNegative || raw >= 0)
      ? { value: roundDurationStorage(raw, fmt), error: null }
      : { value: null, error: fmt === "hm" ? HM_ERROR : DEC_ERROR };
  }
  let s = String(raw).trim().replace(/\s+/g, "");
  if (s === "") return { value: null, error: null };
  let sign = 1;
  if (allowNegative && /^[-+]/.test(s)) { sign = s[0] === "-" ? -1 : 1; s = s.slice(1); }
  if (normalizeDurationFormat(fmt) === DURATION_FORMATS.HM) {
    let m = s.match(/^(\d+)$/);
    if (m) return { value: sign * Number(m[1]) || 0, error: null };
    m = s.match(/^(\d*)[:.,](\d{2})$/);
    if (!m) return { value: null, error: HM_ERROR };
    const mins = Number(m[2]);
    if (mins > 59) return { value: null, error: HM_MINUTES_ERROR };
    const value = roundDurationStorage(Number(m[1] || 0) + mins / 60, "hm");
    return { value: value === 0 ? 0 : sign * value, error: null };
  }
  if (!/^\d+([.,]\d{0,4})?$/.test(s)) return { value: null, error: DEC_ERROR };
  const n = Number(s.replace(",", "."));
  if (!Number.isFinite(n)) return { value: null, error: DEC_ERROR };
  const value = roundDurationStorage(n, "decimal");
  return { value: value === 0 ? 0 : sign * value, error: null };
}

/** Parse permissivo per calcoli interni della griglia: valori non validi → 0. */
export function durationToHours(raw, fmt = DEFAULT_DURATION_FORMAT) {
  if (typeof raw === "number") return Number.isFinite(raw) ? raw : 0;
  const { value } = parseDuration(raw, fmt, { allowNegative: true });
  return value ?? 0;
}

/**
 * Visualizzazione. "hm" arrotonda al minuto con riporto: mai 7:60 / 3:75.
 * opts.signed → prefisso "+" per i positivi; opts.empty → stringa per null/NaN.
 */
export function formatDuration(hours, fmt = DEFAULT_DURATION_FORMAT, { signed = false, empty } = {}) {
  const n = Number(hours);
  if (hours === null || hours === undefined || hours === "" || !Number.isFinite(n)) {
    if (empty !== undefined) return empty;
  }
  const v = Number.isFinite(n) ? n : 0;
  let out;
  let positive;
  if (normalizeDurationFormat(fmt) === DURATION_FORMATS.HM) {
    const total = Math.round((Math.abs(v) + Number.EPSILON) * 60);
    positive = total > 0 && v > 0;
    out = `${total > 0 && v < 0 ? "-" : ""}${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
  } else {
    out = v.toFixed(2).replace(".", ",");
    positive = v > 0;
    if (/^-0,00$/.test(out)) out = "0,00";
  }
  return signed && positive ? `+${out}` : out;
}

/** Filtro caratteri durante la digitazione (nessuna conversione). */
export function sanitizeDurationTyping(raw, fmt = DEFAULT_DURATION_FORMAT, { allowNegative = false } = {}) {
  const allowed = normalizeDurationFormat(fmt) === DURATION_FORMATS.HM ? /[^\d.,:-]/g : /[^\d.,-]/g;
  let s = String(raw ?? "").replace(allowed, "");
  if (!allowNegative) s = s.replace(/-/g, "");
  else s = s.replace(/(?!^)-/g, "");
  return s;
}

export const durationPlaceholder = (fmt) => (normalizeDurationFormat(fmt) === DURATION_FORMATS.HM ? "0:00" : "0,00");
